/*
 * File: ui-form-quick-table.ts
 * File Created: 2024-11-25 10:34:24
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 12:31:10
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { IQuickUnit, ObjectHelper, QuickHelper } from "@root/core";
import {
    BaseUITag,
    EUITagRuleType,
    EUITagTarget,
    IUITagInput,
    IUITagResult,
    IUITagMoreTagsUnit,
    UITag,
    EUITagRuleUnion,
} from "../core";
import { getMoreTagsFromType } from "../ui-type";

// @uiFormQuickTable - make `iv-form-quick-table` easily handle internal attrs.

interface IUIFormQuickTable {
    /// the `quick` this `iv-form-quick-table` is using.
    quick: IQuickUnit;
    /// the `quick` parent is using. for handling parent relationship.
    quickParent: IQuickUnit;
}

// /// @uiType - iv-form-quick-table
// /// @uiQuicks - (o) => ({ quick: quicks().PropertyShared })
/// @uiRelationCreate
/// @uiAttrs - (o) => ({ parent: { property: o.row.id ?? null } })
/// @uiAttrs - (o) => ({ uiTags: { C: { uiAttrs: (o) => o.name === "property" ? { placeholder: "已自動帶入" } : null } } })
/// @uiAttrs - (o) => ({ uiTags: { U: { uiAttrs: (o) => { console.log("hihi", o); return o.name === "property" ? { placeholder: "已自動帶入" } : null } } } })
/// @uiAttrs - (o) => ({ uiTags: { C: { uiRequired: (o) => o.name === "property" ? false : "inherit" } } })
/// @uiAttrs - (o) => ({ uiTags: { C: { uiDisabled: (o) => o.name === "property" ? true : "inherit" } } })

@UITag.set()
export class uiFormQuickTable extends BaseUITag {
    appliedTo: EUITagTarget[] = [EUITagTarget.FormElement, EUITagTarget.FormSubmit];
    ruleTypes: EUITagRuleUnion[] = [EUITagRuleType.Object];
    allowNullRule: boolean = false;

    parse(input: IUITagInput): IUITagResult | null {
        let rule = this.parseRule(input) as IUIFormQuickTable;
        const { quick, quickParent } = rule;

        if (quick == null || quickParent == null) {
            console.error(
                `<uiFormQuickTable> should contain both :quick and :quick-parent.`,
            );
            return null;
        }

        let generalizedQuick = QuickHelper.generalize(quick);
        let generalizedQuickParent = QuickHelper.generalize(quickParent, {
            plural: false,
        });

        /// 1) make type as `iv-form-quick-table`.
        /// 2) send rule to `@uiQuicks`.
        /// 3) add @uiRelationCreate.
        /// 4) send `:parent` into `iv-form-quick-table`.
        /// 5) set `:ui-tags` for child table.

        let moreTags: IUITagMoreTagsUnit[] = [];
        moreTags.push(
            {
                tag: "uiType",
                rule: "iv-form-quick-table",
            },
            {
                tag: "uiQuicks",
                rule,
            },
            {
                tag: "uiRelationCreate",
                rule: true,
            },
            /// @uiAttrs - (o) => ({ parent: { property: o.row.id ?? null } })
            /// @uiAttrs - (o) => ({ uiTags: { C: { uiAttrs: (o) => o.name === "property" ? { placeholder: "已自動帶入" } : null } } })
            /// @uiAttrs - (o) => ({ uiTags: { U: { uiAttrs: (o) => { console.log("hihi", o); return o.name === "property" ? { placeholder: "已自動帶入" } : null } } } })
            /// @uiAttrs - (o) => ({ uiTags: { C: { uiRequired: (o) => o.name === "property" ? false : "inherit" } } })
            /// @uiAttrs - (o) => ({ uiTags: { C: { uiDisabled: (o) => o.name === "property" ? true : "inherit" } } })
            {
                tag: "uiAttrs",
                rule: (o) => ({
                    parent: { name: generalizedQuickParent.propertyName, id: o.row.id },
                }),
            },
            {
                tag: "uiAttrs",
                rule: (o) => ({
                    uiTags: {
                        C: {
                            uiAttrs: (o) => {
                                let result: any[] = [];
                                /// handle placeholder
                                result.push(
                                    o.name === generalizedQuickParent.propertyName &&
                                        o.value == null
                                        ? {
                                              placeholder: "w_AutofillAfterCreate",
                                              data: {
                                                  "text-not-available":
                                                      "w_AutofillAfterCreate",
                                              },
                                          }
                                        : null,
                                );

                                return ObjectHelper.deepMerge(
                                    {},
                                    ...result.filter((o) => o),
                                );
                            },
                            uiRequired: (o) => {
                                return o.name === generalizedQuickParent.propertyName
                                    ? false
                                    : "inherit";
                            },
                            uiDisabled: (o) => {
                                return o.name === generalizedQuickParent.propertyName
                                    ? true
                                    : "inherit";
                            },
                        },
                        U: {
                            uiAttrs: (o) => {
                                let result: any[] = [];
                                /// handle placeholder
                                result.push(
                                    o.name === generalizedQuickParent.propertyName &&
                                        o.value == null
                                        ? { placeholder: "w_AutofillAfterCreate" }
                                        : null,
                                );
                                return ObjectHelper.deepMerge(
                                    {},
                                    ...result.filter((o) => o),
                                );
                            },
                            uiRequired: (o) => {
                                return o.name === generalizedQuickParent.propertyName
                                    ? false
                                    : "inherit";
                            },
                            uiDisabled: (o) => {
                                return o.name === generalizedQuickParent.propertyName
                                    ? true
                                    : "inherit";
                            },
                        },
                        R: {
                            uiTextEmpty: (o) => {
                                return o.name === "createdAt" || o.name === "updatedAt"
                                    ? "w_AutofillAfterCreate"
                                    : null;
                            },
                        },
                    },
                }),
            },
        );
        return {
            specials: {
                moreTags,
            },
        };
    }
}
